import { gql } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Switch, TableCell, TableRow, TextField } from "@mui/material";
import { omit } from "lodash";
import { useState } from "react";
import { useCreateCancellationReasonMutation, useUpdateCancellationReasonMutation } from "~/graphql/__generated__/graphql";
import { OrgConfig } from "~/providers/OrgConfigProvider";

type CancellationReason = NonNullable<OrgConfig["visitCancellationReasons"]["string"]>

interface Props {
  reason: CancellationReason
}

const CancellationReasonRow = ({ reason }: Props) => {
  const [active, setActive] = useState(reason.active);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [update, { loading }] = useUpdateCancellationReasonMutation();

  return <TableRow>
    <TableCell
      sx={{
        width: '70%',
        maxWidth: '500px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>{reason.description || 'No description'}</span>
        <IconButton
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{ minWidth: 'auto', padding: '4px' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </div>
    </TableCell>
    <TableCell sx={{ width: '15%', maxWidth: '120px' }}>
      {active ?
        <Chip label="Active" color="success" size="small" />
      :
        <Chip label="Inactive" size="small" />
      }
    </TableCell>
    <TableCell sx={{ width: '15%', maxWidth: '100px' }}>
      <Switch checked={active} disabled={loading} onChange={() => {
        setActive(!active)
        update({ variables: { ...reason, active: !active }})
      }} size="small" />
    </TableCell>

    {dialogOpen && <CancellationReasonDialog onClose={() => setDialogOpen(false)} reason={reason} />}
  </TableRow>
}

export default CancellationReasonRow;


interface CancellationReasonDialogProps {
  onClose: () => void;
  reason: { id: number, description: string, active: boolean };
}

export const CancellationReasonDialog = ({
  onClose,
  reason,
}: CancellationReasonDialogProps) => {
  const [local, setLocal] = useState(reason);
  const [create, { loading: createLoading }] = useCreateCancellationReasonMutation();
  const [update, { loading: updateLoading }] = useUpdateCancellationReasonMutation();

  const loading = createLoading || updateLoading

  const handleSave = async () => {
    if (local.id) {
      await update({ variables: local })
    } else {
      await create({
        variables: omit(local, "id"),
        awaitRefetchQueries: true,
        refetchQueries: ["OrgConfig"],
      })
    }

    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {reason ? 'Edit Cancellation Reason' : 'Add Cancellation Reason'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Description"
          fullWidth
          variant="outlined"
          value={local.description}
          onChange={(e) => setLocal({ ...local, description: e.target.value })}
          disabled={loading}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={local.active}
              onChange={(e) => setLocal({ ...local, active: e.target.checked })}
              disabled={loading}
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          loading={loading}
          disabled={!local.description?.trim()}
        >
          {loading ? (reason ? 'Updating...' : 'Creating...') : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

gql`
  mutation CreateCancellationReason($description: String!, $active: Boolean) @permission(requires: manageOrgConfig) {
    createVisitCancellationReason(description: $description, active: $active) {
      id
    }
  }
`;

gql`
  mutation UpdateCancellationReason($id: Int!, $description: String, $active: Boolean) @permission(requires: manageOrgConfig) {
    updateVisitCancellationReason(id: $id, description: $description, active: $active) {
      id
      description
      active
    }
  }
`;
